import React from 'react'
import { useTranslation } from 'react-i18next'
import { Award, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { BadgePicker } from '@/components/features/badges/BadgePicker'
import { useBadgeCatalog } from '@/components/features/badges/badgeCatalog'
import { CriteriaFields, useAudienceOptions } from '@/components/features/campaigns/AudiencePicker'
import { apiMessage } from '@/components/features/bookings/bookingsUtils'
import { sdk } from '@/lib/sdk'
import type { NetworkingHostRule } from '@/types/networking'

/**
 * F-176 (David, 2026-10-29): who may ask to be a mentor, besides the roles.
 * The groups and tags are the campaign audience selectors; the badges are the
 * badge catalog's picker (private badges count). It saves on its own endpoint
 * (`PUT /api/networking/host-rule`), so it shows only in the space edit.
 */
export const NetworkingHostRuleSettings = ({ spaceId, host, disabled }: { spaceId: string; host: string; disabled?: boolean }) => {
  const { t } = useTranslation('networking')
  const options = useAudienceOptions()
  const catalog = useBadgeCatalog()
  const [saved, setSaved] = React.useState<NetworkingHostRule | null>(null)
  const [groupIds, setGroupIds] = React.useState<string[]>([])
  const [tags, setTags] = React.useState<string[]>([])
  const [badges, setBadges] = React.useState<NetworkingHostRule['badges']>([])
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [done, setDone] = React.useState(false)

  const load = React.useCallback((rule: NetworkingHostRule | undefined) => {
    const r = rule ?? { groupIds: [], tags: [], badgeIds: [], groups: [], badges: [], updatedAt: null }
    setSaved(r); setGroupIds(r.groupIds); setTags(r.tags); setBadges(r.badges)
  }, [])

  React.useEffect(() => {
    let alive = true
    sdk.networking.config(spaceId).then((res) => { if (alive) load(res.hostRule) }).catch((err: unknown) => { if (alive) setError(apiMessage(err, t('hostRule.error'))) })
    return () => { alive = false }
  }, [spaceId, load, t])

  const restricted = groupIds.length + tags.length + badges.length > 0
  const dirty = saved !== null && (
    groupIds.join() !== saved.groupIds.join() || tags.join() !== saved.tags.join() || badges.map((b) => b.id).join() !== saved.badgeIds.join())

  const save = async () => {
    setBusy(true); setError(null); setDone(false)
    try {
      load(await sdk.networking.setHostRule({ spaceId, groupIds, tags, badgeIds: badges.map((b) => b.id) }))
      setDone(true)
    } catch (err) {
      setError(apiMessage(err, t('hostRule.error')))
    } finally {
      setBusy(false)
    }
  }

  const off = disabled || busy || saved === null
  return (
    <div className="space-y-3 rounded-lg bg-gray-50 p-3" data-testid="networking-host-rule">
      <div>
        <p className="text-sm font-medium text-gray-700">{t('hostRule.title', { host })}</p>
        <p className="text-xs text-gray-500">{restricted ? t('hostRule.hintOn') : t('hostRule.hintOff')}</p>
      </div>
      <CriteriaFields idPrefix="networking-host-rule" showPeople={false} disabled={off}
        groups={options.groups} tagOptions={options.tagOptions} noAccess={options.noAccess}
        value={{ groupIds, tags, people: [] }} onChange={(c) => { setGroupIds(c.groupIds); setTags(c.tags); setDone(false) }} />
      <div className="space-y-2" role="group" aria-labelledby="networking-host-rule-badges">
        <h4 id="networking-host-rule-badges" className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Award className="h-4 w-4 text-gray-400" />{t('hostRule.badges')}
        </h4>
        {badges.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {badges.map((b) => (
              <li key={b.id} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white">
                {b.name}
                <button type="button" disabled={off} aria-label={t('hostRule.removeBadge', { name: b.name })} className="rounded hover:bg-white/20"
                  onClick={() => { setBadges(badges.filter((x) => x.id !== b.id)); setDone(false) }}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <BadgePicker value={null} disabled={off} exclude={badges.map((b) => b.id)} testId="networking-host-rule-badge"
          clearLabel={t('hostRule.addBadge')}
          onChange={(id) => {
            const b = catalog?.find((x) => x.id === id)
            if (!b) return
            setBadges((cur) => (cur.some((x) => x.id === b.id) ? cur : [...cur, { id: b.id, name: b.name, imageUrl: b.imageUrl ?? null, visibility: b.visibility === 'private' ? 'private' : 'public' }]))
            setDone(false)
          }} />
      </div>
      <p className="text-xs text-gray-500">{t('hostRule.override', { host })}</p>
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" disabled={off || !dirty} onClick={() => void save()} data-testid="networking-host-rule-save" className="gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t('hostRule.save')}
        </Button>
        {done && !dirty && <span className="text-sm text-green-700" role="status">{t('hostRule.saved')}</span>}
      </div>
    </div>
  )
}
